import { Log } from "oidc-client-ts";

/**
 * Диагностика и хелперы для надёжного обновления OIDC-токена.
 *
 * Контекст: react-oidc-context считает `isAuthenticated = есть юзер И НЕ
 * истёк access-токен`. Поэтому ЛЮБОЙ пропущенный/упавший silent-renew
 * мгновенно роняет UI в «Войти», хотя юзер ещё в хранилище. Главные причины
 * «рандомных» вылетов — задушенные таймеры фоновой вкладки (renew не успел до
 * экспирации) и гонки ротации refresh-токена между вкладками. Эти хелперы
 * ловят причину (буфер + логи) и питают watchdog, который догоняет renew.
 */

export type AuthDiagEntry = { t: string; type: string; detail?: string };

const RING_SIZE = 60;
const buffer: AuthDiagEntry[] = [];

/** Записать событие в кольцевой буфер + консоль. Буфер висит на
 *  `window.__mtAuthDiag` — после случайного вылета можно открыть консоль
 *  ОДИН раз и прочитать историю, не сидя с DevTools заранее. */
export function recordDiag(type: string, detail?: string): void {
  const entry: AuthDiagEntry = {
    t: new Date().toISOString(),
    type,
    ...(detail ? { detail } : {}),
  };
  buffer.push(entry);
  if (buffer.length > RING_SIZE) buffer.shift();
  // eslint-disable-next-line no-console
  console.info(`[auth] ${entry.t} ${type}${detail ? " — " + detail : ""}`);
  if (typeof window !== "undefined") {
    (window as unknown as { __mtAuthDiag?: AuthDiagEntry[] }).__mtAuthDiag =
      buffer;
  }
}

/** Включить внутренние логи oidc-client-ts (планирование/ошибки renew) в dev
 *  или по флагу VITE_OIDC_LOG=1 — чтобы видеть весь жизненный цикл токена. */
export function enableOidcLogging(): void {
  if (import.meta.env.DEV || import.meta.env.VITE_OIDC_LOG === "1") {
    Log.setLogger(console);
    Log.setLevel(Log.INFO);
  }
}

/** Бэкофф с джиттером для ретраев silent-renew. Джиттер (диапазон [exp/2, exp])
 *  де-синхронизирует вкладки, чтобы повторные обновления не сталкивались снова
 *  на ротации refresh-токена. `rand` инъектируется для тестируемости. */
export function renewRetryDelayMs(
  attempt: number,
  rand: () => number = Math.random,
  baseMs = 1000,
  maxMs = 15000,
): number {
  const exp = Math.min(maxMs, baseMs * 2 ** Math.max(0, attempt));
  const half = exp / 2;
  return Math.round(half + half * rand());
}

/** Истёк ли токен или истекает в пределах `skewSec` — нужно ли обновлять
 *  заранее (напр. при возврате на вкладку после фонового простоя). */
export function shouldRenewSoon(
  expiresAtSec: number | undefined,
  nowSec: number,
  skewSec = 60,
): boolean {
  if (!expiresAtSec) return false;
  return expiresAtSec - nowSec <= skewSec;
}
